import { getApiErrorMessage } from '@api/getApiError';
import { serviceSlice } from '@api/Trinium/Endpoints/Service/slice';
import { Service } from '@api/Trinium/Endpoints/Service/types';
import { store } from '@reduxStore';
import { Field, useFormikContext } from 'formik';
import { Autocomplete, TextField } from '@mui/material';
import { useState } from 'react';

interface ServiceDropdownFieldProps {
	fieldName: string;
	className?: string;
	disabled?: boolean;
}

/**
 * This servie lookup function is more generic that the jobCategories/{id}/services endpoint, this allows lookup
 * accross all job categories.  Only services that the current user has acces to view will be returned.
 * @param props fieldName to store selected services in (Service array), optional css className and the dropdown
 * can be disabled.
 * @returns Provides a dropdown of available serviecs.
 */
export default function ServiceDropdownField(props: ServiceDropdownFieldProps) {
	const { setFieldValue, getFieldMeta } = useFormikContext();
	const { value } = getFieldMeta<Service[]>(props.fieldName);
	const [options, setOptions] = useState<Service[]>([]);
	const [loading, setLoading] = useState(false);

	const handleSearch = async (searchValue: string) => {
		if (!searchValue) {
			setOptions([]);
			return;
		}
		setLoading(true);
		try {
			const services = await onServiceSearch(searchValue);
			setOptions(services);
		} catch (error) {
			console.error('Error searching services:', error);
			setOptions([]);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Field name={props.fieldName}>
			{() => (
				<Autocomplete
					multiple
					options={options.filter((option) => !value.some((selected) => selected.id === option.id))}
					value={value}
					onChange={(_, newValue) => {
						void setFieldValue(props.fieldName, newValue);
					}}
					onInputChange={(_, newInputValue) => {
						if (newInputValue.length >= 3) {
							void handleSearch(newInputValue);
						} else {
							setOptions([]);
						}
					}}
					getOptionLabel={getServiceItemText}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					loading={loading}
					disabled={props.disabled}
					disableCloseOnSelect
					className={props.className}
					fullWidth
					renderInput={(params) => (
						<TextField
							{...params}
							variant='standard'
							label='Services'
							multiline
							fullWidth
						/>
					)}
				/>
			)}
		</Field>
	);
}

/**
 * Callback for the Button Popup Content to display the service as a list item
 */
const getServiceItemText = (service: Service) => `${service.name}(${service.id})`;

/**
 * Callback for the content to search when the user types into the input
 */
const onServiceSearch = (searchValue: string) => {
	const terminalNumber = store.getState().applicationState.selectedTerminal?.number;
	return new Promise<Service[]>((resolve, reject) => {
		store
			.dispatch(
				serviceSlice.endpoints.fetchAllServices.initiate({
					nameStartsWith: searchValue,
					terminalNumber: terminalNumber ?? ''
				})
			)
			.unwrap()
			.then((response) => {
				resolve(response);
			})
			.catch((error: unknown) => {
				const deserializedError = getApiErrorMessage(error);
				reject(new Error(deserializedError));
			});
	});
};
